import { InShort, RelatedPages } from "@marketing/components/answer";
import { Article } from "@marketing/components/article";
import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
	Steps,
} from "@marketing/components/blocks";
import { BRAND_PATHS, type BrandIcon } from "@marketing/components/brand-icons";
import { FlowDiagram } from "@marketing/components/FlowDiagram";
import { Chevron, mkButton, mkLink } from "@marketing/components/MkButton";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "Which ecommerce platforms does it connect to?",
		a: "Salla, Zid, Shopify and WooCommerce out of the box, kept current by their webhooks. Any platform that can produce a product feed in JSON lines with Google Merchant fields works too, and for others we build a connector to your platform's API.",
	},
	{
		q: "Does it work with Salla or Zid?",
		a: "Yes. A Salla store connects by installing the Hephon app from Salla, and a Zid store by approving the Hephon app in Zid. Products and variants come in Arabic and English, with stock per branch or location, kept current by the platform's webhooks and a daily refresh.",
	},
	{
		q: "Can my developers push products directly?",
		a: "Yes. The REST API accepts products, prices and stock per branch, with scoped API keys and idempotent writes. The OpenAPI description is public.",
	},
	{
		q: "How often is the catalog refreshed?",
		a: "On the schedule you set for each source, from hourly to weekly for a full refresh, with change pulls as often as every 15 minutes. Shopify and WooCommerce webhooks start a change pull when a product is edited, and the API updates prices and stock as soon as you send them.",
	},
	{
		q: "Where do orders go?",
		a: "In your app, the basket goes to your own checkout. Orders taken on WhatsApp or by phone go to your platform through its API when we have built ordering for it, to your own server through our Orders API, or to your team as an order ticket in the dashboard.",
	},
	{
		q: "How are webhooks secured?",
		a: "Each delivery is signed with a secret only you and we hold, carries an id and a timestamp, and is retried with growing waits if your server does not answer. You choose which events each endpoint receives.",
	},
	{
		q: "Is search better than our current site search?",
		a: "It combines keyword and meaning search across your whole catalog and checks each result against what the shopper asked, in Arabic and English.",
	},
	{
		q: "How long does connecting take?",
		a: "A Shopify or WooCommerce store, or a product feed, is usually connected in days. A custom connector to your own platform takes longer; we scope it with your team.",
	},
];

const SOURCES: { name: string; text: string; brand?: BrandIcon }[] = [
	{
		name: "Shopify",
		text: "Products and variants, kept current by webhooks.",
		brand: "shopify",
	},
	{
		name: "WooCommerce",
		text: "Products and variations, kept current by webhooks.",
		brand: "woocommerce",
	},
	{
		name: "Salla",
		text: "Products, variants and stock per branch, in Arabic and English.",
	},
	{
		name: "Zid",
		text: "Products, variants and stock per location, in Arabic and English.",
	},
	{
		name: "Google product feed",
		text: "JSON lines with Google Merchant fields, from a URL or an upload.",
		brand: "google",
	},
	{
		name: "Push API",
		text: "Your systems send products, prices and stock per branch.",
	},
	{
		name: "Your platform",
		text: "A connector to your platform's API, built by our team.",
	},
];

const CATALOG = [
	{
		title: "Per-branch data",
		text: "Each branch keeps its own prices and stock, and the assistant answers from the shopper's branch.",
	},
	{
		title: "Refreshed on a schedule",
		text: "Catalog refreshes run in the background on the schedule you set, never in the way of a shopper.",
	},
	{
		title: "Understood, not just copied",
		text: "Every product gets search names in each language your shoppers use, so “7aleeb” finds milk.",
	},
];

const DEVELOPERS = [
	{
		title: "REST API",
		text: "Catalog, conversations and orders over HTTPS, with errors in a standard problem format.",
	},
	{
		title: "Scoped API keys",
		text: "Each key can do only what it is for, and every write accepts an Idempotency-Key.",
	},
	{
		title: "Signed webhooks",
		text: "Order, basket and conversation events delivered to your server, signed and retried.",
	},
	{
		title: "Orders your way",
		text: "Your platform's API, our Orders API on your server, or order tickets for your team.",
	},
];

const STEPS = [
	{
		title: "Pick your source",
		text: "Salla, Zid, Shopify, WooCommerce, a product feed, the API, or a connector we build for your platform. Credentials are encrypted when you save them.",
	},
	{
		title: "Run the first import",
		text: "Products, prices and stock come in branch by branch, and each product is prepared for search. You see every run and every request on the source's page.",
	},
	{
		title: "Set the schedule and orders",
		text: "Choose how often it refreshes and where orders go, then test the assistant on your own catalog.",
	},
];

const RELATED = [
	{ href: "/how-it-works", label: "How search works" },
	{ href: "/channels/in-app", label: "In your app" },
	{ href: "/channels/whatsapp", label: "WhatsApp ordering" },
	{ href: "/security", label: "Security" },
	{ href: "/ai-agent-for-ecommerce", label: "AI agent for ecommerce" },
	{ href: "/supermarkets", label: "For supermarkets" },
];

export const Route = createFileRoute("/_marketing/integrations")({
	head: () =>
		marketingHead({
			title: "Salla, Zid, Shopify & WooCommerce Integrations | Hephon Commerce",
			description:
				"Connect Hephon Commerce to Salla, Zid, Shopify, WooCommerce, a product feed or our REST API: prices and stock per branch, orders back, signed webhooks.",
			path: "/integrations",
			breadcrumb: ["Integrations"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: IntegrationsPage,
});

function IntegrationsPage() {
	return (
		<>
			<PageIntro
				kicker="Connects to the catalog you already run."
				title="Salla, Zid, Shopify, WooCommerce, feed and API integrations"
				lead="Hephon Commerce reads your products, prices and stock from the platform you use today, branch by branch, and sends orders back where your team already works."
			>
				<ul className="mx-auto grid max-w-4xl gap-2 pb-10 text-start sm:grid-cols-2 sm:pb-14 lg:grid-cols-3">
					{SOURCES.map((source) => (
						<li
							key={source.name}
							className="flex items-start gap-3 rounded-[20px] bg-(--mk-white) p-5"
						>
							<span className="grid size-9 shrink-0 place-items-center rounded-xl bg-(--mk-mist)">
								{source.brand ? (
									<svg
										viewBox="0 0 24 24"
										aria-hidden="true"
										className="size-[18px]"
									>
										<path
											d={BRAND_PATHS[source.brand]}
											fill="currentColor"
										/>
									</svg>
								) : (
									<span className="font-semibold text-[13px]">
										API
									</span>
								)}
							</span>
							<span>
								<span className="block font-semibold">
									{source.name}
								</span>
								<span className="mt-0.5 block text-(--mk-muted) text-[15px] leading-snug">
									{source.text}
								</span>
							</span>
						</li>
					))}
				</ul>
			</PageIntro>

			<InShort updated={UPDATED}>
				<p>
					Hephon Commerce connects to a retailer's catalog through
					Shopify, WooCommerce, a product feed in JSON lines with
					Google Merchant fields, a REST API, or a custom connector
					built for the retailer's own platform. Prices and stock are
					kept per branch, and refreshes run in the background on a
					schedule.
				</p>
				<p>
					Orders go to the retailer's own checkout in its app, to its
					platform or server through an API, or to its team as an
					order ticket. Developers get scoped API keys, idempotent
					writes and signed webhooks.
				</p>
			</InShort>

			<Section
				id="flow-title"
				title="Catalog in, orders out."
				lead="One assistant between your systems and your shoppers, on every channel."
				center
			>
				<FlowDiagram named />
			</Section>

			<Article tone="mist">
				<h2 id="sources">Which platforms and feeds can it read?</h2>
				<h3>Shopify</h3>
				<p>
					Products and their variants are read through Shopify's Admin
					API. Each variant becomes a product the assistant can add,
					because a variant is what a basket holds. Shopify's product
					webhooks start a change pull when a product is created or
					edited, and the full refresh removes products you deleted.
				</p>
				<h3>Salla</h3>
				<p>
					The store installs the Hephon app from Salla, and its access
					arrives on its own. Each Salla branch becomes a store with
					its own stock, products are read in Arabic and English with
					every option as an item the assistant can add, and
					categories and barcodes come with them. Salla's product and
					category webhooks start a refresh, and tokens are renewed
					before they expire. Ordering can be turned on for pickup:
					the assistant places an unpaid order at the shopper's branch
					and sends Salla's payment link.
				</p>
				<h3>Zid</h3>
				<p>
					The store approves the Hephon app in Zid. Each Zid location
					becomes a store with its own stock, names come in Arabic and
					English, and variants and nested categories are kept. Hephon
					subscribes to the store's product webhooks so edits start a
					refresh. Ordering can be turned on for delivery: the
					assistant creates the order with Zid's payment link for the
					shopper to pay.
				</p>
				<h3>WooCommerce</h3>
				<p>
					Products and variations are read through the WooCommerce
					REST API with a consumer key. You paste a webhook secret we
					issue into WooCommerce, and each product update it sends
					starts a change pull.
				</p>
				<h3>Product feeds</h3>
				<p>
					A file in JSON lines, one product per line with the field
					names of a Google Merchant feed: id, title, price, sale
					price, availability, brand, GTIN and image links. It can sit
					at an https address we read on your schedule, or be uploaded
					through the API (up to 50 MB, compressed or not). A line can
					name the branch it prices, so one feed can carry every
					branch.
				</p>
				<h3>REST API</h3>
				<p>
					Your systems send products in batches of up to 500, update
					the price and stock of one branch, or stop selling a
					product. Every import returns a run you can follow until it
					is done.
				</p>
				<h3>Custom connectors</h3>
				<p>
					Retailers with their own ordering platforms get a connector
					built by our team to that platform's API. It reads branches,
					products, prices and stock, and can place orders when the
					platform allows it.
				</p>
			</Article>

			<Section
				id="catalog-title"
				title="Your catalog, ready to be asked."
				lead="Connecting is the start. Every product is prepared so shoppers can find it however they ask."
			>
				<DetailGrid items={CATALOG} />
			</Section>

			<Article tone="mist">
				<h2 id="sync">How does catalog syncing work?</h2>
				<p>
					Each source has its own schedule: a full refresh hourly,
					every six hours, daily or weekly, and change pulls as often
					as every 15 minutes. Refreshes run in a background worker,
					never inside a shopper's request, so a large import does not
					slow anyone's chat. After products come in, new and changed
					ones are prepared for search: they get search names in each
					language your shoppers use, and are checked against your
					content rules. The source's page shows every run, how many
					products changed, and each request it made to your platform.
				</p>

				<h2 id="branches">
					How are prices and stock handled per branch?
				</h2>
				<p>
					Every branch is a store with its own country, currency and
					timezone, and each product has its own price and stock in
					each branch. The shopper sees what their branch can deliver,
					in its currency. A Shopify or WooCommerce shop comes in as
					one store; retailers with branch-level prices send them
					through a feed, the API or a custom connector.
				</p>

				<h2 id="orders">Where do orders go?</h2>
				<p>
					Inside your app or website, the assistant hands the basket
					to your own checkout, and your platform takes the order as
					it does today. Your platform can report those orders through
					the API, so you see which ones the assistant helped with.
				</p>
				<p>
					On WhatsApp and phone calls, where the order is placed
					through Hephon Commerce once the shopper confirms, there are
					three ways. Your platform's API, through a connector that
					supports ordering. Your own server, through our Orders API,
					which your developers implement. Or an order ticket your
					team handles in the Orders screen. Every placement carries
					an idempotency key, so a retry never places an order twice.
				</p>

				<h2 id="api">What does the API offer developers?</h2>
				<p>
					A REST API under /v1 with an OpenAPI description. Keys are
					scoped (catalog, orders, sessions, shopper data and more),
					every write accepts an Idempotency-Key header, and errors
					come back in the standard problem format. Your backend can
					open a shopper session, read a conversation and its basket,
					report orders and delete a shopper's data. Webhooks send
					basket changes, checkout requests, hand-overs to a person,
					staff replies, order events and completed deletions. Each
					delivery is signed, timestamped and retried with growing
					waits.
				</p>

				<h2 id="does-not">What it does not do</h2>
				<ul>
					<li>
						The Shopify and WooCommerce connectors read your
						catalog. They do not place orders in those stores today:
						WhatsApp and phone orders arrive as order tickets or
						through the Orders API.
					</li>
					<li>
						Feeds are read in JSON lines. An XML or spreadsheet feed
						needs converting first.
					</li>
					<li>
						Stock is as fresh as your last refresh or API update. It
						does not read your stock live during each chat.
					</li>
				</ul>

				<h2 id="not-fit">When Hephon Commerce is not the right fit</h2>
				<ul>
					<li>
						You have no online catalog with prices and stock, and no
						way to export one.
					</li>
					<li>
						You need an integration on day one with a platform we
						have no connector for, and cannot produce a feed.
					</li>
				</ul>
			</Article>

			<Section
				id="developers-title"
				tone="night"
				title="An API built for retail."
				lead="Documented and predictable, for the teams who run your platform."
			>
				<DetailGrid items={DEVELOPERS} columns={4} tone="night" />
				<div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-3">
					<a
						href="/openapi.json"
						className={mkButton({ variant: "night" })}
					>
						Open the OpenAPI description
					</a>
					<a
						href="/demo"
						className={mkLink("text-(--mk-signal-night)")}
					>
						Talk to our engineers
						<Chevron />
					</a>
				</div>
			</Section>

			<Section id="steps-title" title="Live in three steps." center>
				<Steps items={STEPS} />
			</Section>

			<Faq items={FAQ} title="Integrations, answered." />
			<RelatedPages links={RELATED} />
			<Closing />
		</>
	);
}
